import { getAdministrativeArea } from 'src/careflow/care-coordination/referral/components/workflow-step/AddressChecklistItem/Map';
import { Address, Extension } from 'src/careflow/ph-core/fhir-client/resources';

export interface AdministrativeField {
  name: string;
  visible?: boolean;
  label?: string;
  googleComponent?: string;
  fhirField?: string;
  saveToFhir?: boolean;
  extensionUrl?: string;
}

export interface AddressMappingConfig {
  administrativeFields?: AdministrativeField[];
}

const DEFAULT_COUNTY_FIELD: AdministrativeField = {
  name: 'county',
  visible: true,
  label: 'County',
  googleComponent: 'administrative_area_level_2',
  fhirField: 'district',
  saveToFhir: true
};

const STANDARD_FHIR_FIELDS = {
  district: 'district',
  state: 'state',
  city: 'city',
  country: 'country',
  postalCode: 'postalCode'
} as const;

const isExtensionField = (fhirField?: string): boolean => {
  return fhirField?.startsWith('extension.') ?? false;
};

const createExtension = (url: string, value: string): Extension => ({
  url,
  valueString: value
});

const updateOrAddExtension = (extensions: Extension[], extension: Extension): Extension[] => {
  const index = extensions.findIndex(ext => ext.url === extension.url);
  if (index >= 0) {
    extensions[index] = extension;
  } else {
    extensions.push(extension);
  }
  return extensions;
};

/**
 * Transform Google Places API data to FHIR address based on tenant configuration
 * @param googlePlaceData - Google Places API result
 * @param config - Administrative fields configuration
 * @param baseAddress - Base address object to enhance
 * @returns FHIR Address object with mapped administrative data
 */
export const transformGooglePlaceToFhirAddress = (
  googlePlaceData: any,
  config: AddressMappingConfig,
  baseAddress: Partial<Address>
): Address => {
  const address = { ...baseAddress };

  if (!googlePlaceData?.address_components) {
    return address as Address;
  }

  const fieldsToProcess = config.administrativeFields?.length 
    ? config.administrativeFields 
    : [DEFAULT_COUNTY_FIELD];

  fieldsToProcess.forEach(field => {
    if (!field.googleComponent || !field.fhirField) {
      return;
    }

    const value = getAdministrativeArea(
      googlePlaceData.address_components,
      field.googleComponent
    );

    if (!value) {
      return;
    }

    if (isExtensionField(field.fhirField) && field.extensionUrl) {
      if (!address.extension) {
        address.extension = [];
      }
      const extension = createExtension(field.extensionUrl, value);
      updateOrAddExtension(address.extension, extension);
    } else if (field.fhirField in STANDARD_FHIR_FIELDS) {
      (address as any)[field.fhirField] = value;
    }
  });

  return address as Address;
};

/**
 * Get UI configuration for administrative fields
 * @param config - Administrative fields configuration
 * @returns Array of visible fields for UI rendering
 */
export const getVisibleAdministrativeFields = (
  config: AddressMappingConfig
): AdministrativeField[] => {
  if (!config.administrativeFields) {
    return [DEFAULT_COUNTY_FIELD];
  }

  return config.administrativeFields.filter(field => field.visible !== false);
};

/**
 * Filter address data before saving to FHIR based on saveToFhir configuration
 * @param address - Address object to filter
 * @param config - Administrative fields configuration
 * @returns Filtered Address object
 */
export const filterAddressForFhirSave = (
  address: Address,
  config: AddressMappingConfig
): Address => {
  if (!config.administrativeFields) {
    return address;
  }

  const filteredAddress = { ...address };

  config.administrativeFields.forEach(field => {
    if (field.saveToFhir !== false || !field.fhirField) {
      return;
    }

    if (isExtensionField(field.fhirField) && field.extensionUrl) {
      if (filteredAddress.extension) {
        filteredAddress.extension = filteredAddress.extension.filter(
          ext => ext.url !== field.extensionUrl
        );
        if (filteredAddress.extension.length === 0) {
          filteredAddress.extension = undefined;
        }
      }
    } else if (field.fhirField === 'district') {
      filteredAddress.district = undefined;
    }
  });

  return filteredAddress;
};


/**
 * Get value from FHIR Address for a given administrative field configuration
 * This helper extracts values from either standard fields or extensions
 * @param address - FHIR Address object
 * @param field - Administrative field configuration
 * @returns Extracted value or undefined
 */
export const getAddressFieldValue = (
  address: Address,
  field: AdministrativeField
): string | undefined => {
  if (!address || !field.fhirField) {
    return undefined;
  }

  if (isExtensionField(field.fhirField) && field.extensionUrl) {
    const extension = address.extension?.find(ext => ext.url === field.extensionUrl);
    
    if (extension) {
      if (extension.valueString) return extension.valueString;
      if (extension.valueCode) return extension.valueCode;
      if (extension.valueInteger !== undefined) return String(extension.valueInteger);
      if (extension.valueDecimal !== undefined) return String(extension.valueDecimal);
      if (extension.valueBoolean !== undefined) return String(extension.valueBoolean);
    }
    return undefined;
  }

  if (field.fhirField in STANDARD_FHIR_FIELDS) {
    return (address as any)[field.fhirField];
  }

  return undefined;
};

/**
 * Validate administrative fields configuration
 * @param config - Configuration to validate
 * @returns Validation result
 */
export const validateAdministrativeFieldsConfig = (
  config: AddressMappingConfig
): { valid: boolean; errors: string[] } => {
  if (!config.administrativeFields) {
    return { valid: true, errors: [] };
  }

  const errors: string[] = [];

  config.administrativeFields.forEach((field, index) => {
    if (!field.name) {
      errors.push(`Field at index ${index} missing required 'name' property`);
    }
    if (field.saveToFhir && (!field.googleComponent || !field.fhirField)) {
      errors.push(`Field '${field.name}' is configured to save to FHIR but missing googleComponent or fhirField`);
    }
    if (isExtensionField(field.fhirField) && !field.extensionUrl) {
      errors.push(`Field '${field.name}' uses extension but missing required 'extensionUrl' property`);
    }
  });

  return {
    valid: errors.length === 0,
    errors
  };
};
